import { formatCurrencyWhole } from '../../utils/formatCurrency.js';

const money = (value) => formatCurrencyWhole(value);

/**
 * Flat commission / remittance ledger — no nested cards.
 */
export default function AgentCommissionLedger({
  totalFreight,
  toPay,
  commissionAmount,
  commissionPct,
  commissionStatus,
  tdsAmount,
  tdsPct,
  netToAgent,
  netDue,
  netDueLabel,
  outstanding,
  shipmentCount,
  positiveTone = 'rose',
}) {
  const hasCommission = Number(commissionAmount || 0) > 0;
  const hasTds = Number(tdsAmount || 0) > 0;
  const rows = [];

  if (totalFreight != null && totalFreight !== '') {
    rows.push({ label: 'Total freight', value: money(totalFreight) });
  }
  if (toPay != null && toPay !== '') {
    rows.push({ label: 'To-Pay', value: money(toPay) });
  }
  if (hasCommission) {
    rows.push({
      label: `Commission (${commissionPct ?? 0}%)`,
      value: `− ${money(commissionAmount)}`,
      muted: true,
      hint: commissionStatus
        ? (String(commissionStatus).toUpperCase() === 'PAID' ? 'Paid' : 'Unpaid')
        : null,
    });
  }
  if (hasTds) {
    rows.push({
      label: `TDS (${tdsPct ?? 0}%)`,
      value: `− ${money(tdsAmount)}`,
      muted: true,
    });
    rows.push({
      label: 'Net to agent',
      value: money(
        netToAgent != null
          ? netToAgent
          : Number(commissionAmount || 0) - Number(tdsAmount || 0),
      ),
      muted: true,
    });
  }

  const remitLabel = netDueLabel
    || (hasTds ? 'Net to remit (To-Pay − net commission)' : 'Net to remit (To-Pay − commission)');

  return (
    <div className="text-sm">
      {rows.map((row) => (
        <div
          key={row.label}
          className={`flex items-center justify-between gap-4 py-1 border-b border-gray-100 ${
            row.muted ? 'text-gray-500' : 'text-gray-700'
          }`}
        >
          <span className="min-w-0">
            {row.label}
            {row.hint ? (
              <span className="ml-1.5 text-[10px] font-medium text-gray-400">{row.hint}</span>
            ) : null}
          </span>
          <span className="tabular-nums whitespace-nowrap font-medium text-gray-900">{row.value}</span>
        </div>
      ))}
      {netDue != null && netDue !== '' && (
        <div className="flex items-center justify-between gap-4 pt-2">
          <span className="font-semibold text-gray-800 min-w-0">{remitLabel}</span>
          <span
            className={`tabular-nums whitespace-nowrap font-semibold ${
              Number(netDue) > 0
                ? (positiveTone === 'emerald' ? 'text-emerald-700' : 'text-rose-700')
                : 'text-emerald-700'
            }`}
          >
            {money(netDue)}
          </span>
        </div>
      )}
      {outstanding != null && outstanding !== '' && Math.abs(Number(outstanding) - Number(netDue)) > 0.5 && (
        <div className="flex items-center justify-between gap-4 pt-1.5 text-gray-600">
          <span>Outstanding</span>
          <span
            className={`tabular-nums font-semibold ${
              Number(outstanding) > 0 ? 'text-rose-700' : 'text-emerald-700'
            }`}
          >
            {money(outstanding)}
          </span>
        </div>
      )}
      {shipmentCount != null && (
        <div className="flex items-center justify-between gap-4 pt-1 text-gray-500">
          <span>Shipments</span>
          <span className="tabular-nums">{shipmentCount}</span>
        </div>
      )}
    </div>
  );
}
